import React, { useState } from 'react';
import {
    Box,
    Flex,
    Input,
    Text,
    Textarea,
    VStack,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalCloseButton,
    Select,
    FormControl,
    FormLabel,
    Button,
} from '@chakra-ui/react';
import BrandButton from '../../../components/brandButton/BrandButton';
import { CustomToast } from '../../../components/toast/CustomToast';
import { mutateFunction, useMutation } from '../../../libs/apis';
import IsDesktop from '../../../utils/IsDesktop';

const CreatePlanModal = ({ onToggle, onSuccess, userTypes = [] }) => {
    const isDesktop = IsDesktop();
    const { mutate, isLoading } = useMutation(mutateFunction);
    const { addToast } = CustomToast();
    const [formData, setFormData] = useState({
        name: '',
        price: '',
        description: '',
        billing_cycle: 'monthly',
        user_type_id: '',
    });
    const [errors, setErrors] = useState({});

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
        // Clear error when user starts typing
        if (errors[name]) {
            setErrors((prev) => ({
                ...prev,
                [name]: '',
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.name.trim()) {
            newErrors.name = 'Plan name is required';
        }

        if (!formData.price || parseFloat(formData.price) < 0) {
            newErrors.price = 'Valid price is required';
        }

        if (!formData.description.trim()) {
            newErrors.description = 'Description is required';
        }

        if (!formData.billing_cycle) {
            newErrors.billing_cycle = 'Billing cycle is required';
        }

        if (!formData.user_type_id) {
            newErrors.user_type_id = 'User type is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!validateForm()) {
            addToast(
                { message: 'Please fill in all required fields correctly' },
                'error',
            );
            return;
        }

        const submitData = {
            name: formData.name.trim(),
            price: parseFloat(formData.price),
            description: formData.description.trim(),
            billing_cycle: formData.billing_cycle,
            user_type_id: parseInt(formData.user_type_id),
        };

        mutate(
            { key: 'admin/subscription/plans', method: 'post', data: submitData },
            {
                onSuccess(res) {
                    addToast(res?.message || 'Plan created successfully');
                    onSuccess();
                },
                onError(err) {
                    const errorMessage =
                        err?.response?.data?.message ||
                        err?.message ||
                        'Failed to create plan';
                    addToast({ message: errorMessage }, 'error');
                },
            },
        );
    };

    return (
        <Modal
            isOpen={true}
            onClose={onToggle}
            size={isDesktop ? 'xl' : 'full'}
            isCentered
        >
            <ModalOverlay />
            <ModalContent maxH={isDesktop ? '90vh' : '100vh'} overflowY="auto">
                <ModalHeader>
                    <Flex justifyContent="space-between" alignItems="center">
                        <Text color="brand.primary" fontWeight={600}>
                            Create New Subscription Plan
                        </Text>
                        <ModalCloseButton />
                    </Flex>
                </ModalHeader>
                <ModalBody pb={6}>
                    <form onSubmit={handleSubmit}>
                        <VStack spacing={4} align="stretch">
                            <FormControl isRequired isInvalid={!!errors.name}>
                                <FormLabel fontSize="14px" mb={2}>
                                    Plan Name <span style={{ color: 'red' }}>*</span>
                                </FormLabel>
                                <Input
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    placeholder="e.g., Starter, Pro, Premium"
                                    borderWidth="1px"
                                    borderColor="brand.primary"
                                    borderRadius="5px"
                                    h="50px"
                                />
                                {errors.name && (
                                    <Text color="red.500" fontSize="12px" mt={1}>
                                        {errors.name}
                                    </Text>
                                )}
                            </FormControl>

                            <FormControl isRequired isInvalid={!!errors.price}>
                                <FormLabel fontSize="14px" mb={2}>
                                    Price ($) <span style={{ color: 'red' }}>*</span>
                                </FormLabel>
                                <Input
                                    name="price"
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={formData.price}
                                    onChange={handleChange}
                                    placeholder="0.00"
                                    borderWidth="1px"
                                    borderColor="brand.primary"
                                    borderRadius="5px"
                                    h="50px"
                                />
                                {errors.price && (
                                    <Text color="red.500" fontSize="12px" mt={1}>
                                        {errors.price}
                                    </Text>
                                )}
                            </FormControl>

                            <FormControl isRequired isInvalid={!!errors.billing_cycle}>
                                <FormLabel fontSize="14px" mb={2}>
                                    Billing Cycle <span style={{ color: 'red' }}>*</span>
                                </FormLabel>
                                <Select
                                    name="billing_cycle"
                                    value={formData.billing_cycle}
                                    onChange={handleChange}
                                    borderWidth="1px"
                                    borderColor="brand.primary"
                                    borderRadius="5px"
                                    h="50px"
                                >
                                    <option value="monthly">Monthly</option>
                                    <option value="yearly">Yearly</option>
                                    <option value="one-time">One-time</option>
                                </Select>
                                {errors.billing_cycle && (
                                    <Text color="red.500" fontSize="12px" mt={1}>
                                        {errors.billing_cycle}
                                    </Text>
                                )}
                            </FormControl>

                            <FormControl isRequired isInvalid={!!errors.user_type_id}>
                                <FormLabel fontSize="14px" mb={2}>
                                    User Type <span style={{ color: 'red' }}>*</span>
                                </FormLabel>
                                <Select
                                    name="user_type_id"
                                    value={formData.user_type_id}
                                    onChange={handleChange}
                                    placeholder="Select user type"
                                    borderWidth="1px"
                                    borderColor="brand.primary"
                                    borderRadius="5px"
                                    h="50px"
                                >
                                    {userTypes.map((userType) => (
                                        <option key={userType.id} value={userType.id}>
                                            {userType.title.charAt(0).toUpperCase() + userType.title.slice(1)}
                                        </option>
                                    ))}
                                </Select>
                                {errors.user_type_id && (
                                    <Text color="red.500" fontSize="12px" mt={1}>
                                        {errors.user_type_id}
                                    </Text>
                                )}
                            </FormControl>

                            <FormControl isRequired isInvalid={!!errors.description}>
                                <FormLabel fontSize="14px" mb={2}>
                                    Description <span style={{ color: 'red' }}>*</span>
                                </FormLabel>
                                <Textarea
                                    name="description"
                                    value={formData.description}
                                    onChange={handleChange}
                                    placeholder="Describe the features and benefits of this plan"
                                    borderWidth="1px"
                                    borderColor="brand.primary"
                                    borderRadius="5px"
                                    minH="120px"
                                />
                                {errors.description && (
                                    <Text color="red.500" fontSize="12px" mt={1}>
                                        {errors.description}
                                    </Text>
                                )}
                            </FormControl>

                            <Flex justifyContent="flex-end" gap={3} mt={4}>
                                <Button
                                    onClick={onToggle}
                                    colorScheme="gray"
                                    variant="outline"
                                    minW="120px"
                                >
                                    Cancel
                                </Button>
                                <BrandButton
                                    type="submit"
                                    text="Create Plan"
                                    isLoading={isLoading}
                                    colorScheme="blue"
                                />
                            </Flex>
                        </VStack>
                    </form>
                </ModalBody>
            </ModalContent>
        </Modal>
    );
};

export default CreatePlanModal;

